import { useLang } from '../../../../../hooks/use-lang';
import { Card } from '../../../../../components/card/card';
import { Container } from '../../../../../components/container/container';
import { Select } from '../../../../../components/select/select';
import {
  EXTERNAL_MONITORING_MIN_DAYS_FOR_MONTH,
  EXTERNAL_MONITORING_MIN_DAYS_FOR_WEEK,
  externalMonitoringRangeDayCountInclusive,
} from '../../../../../helpers/external-monitoring-period';
import type { ExternalResourceCategory } from '../../../../../store/api/monitoring-api';
import type { SourceOption } from './use-dashboard-external-monitoring';
import { ExternalMonitoringPeriodDropdown } from './external-monitoring-period-dropdown';
import s from './dashboard-external-monitoring.module.css';

export type ExternalMonitoringFiltersState = {
  sourceId: string;
  periodBeginSec: number;
  periodEndSec: number;
  groupingInterval: 'day' | 'week' | 'month';
  categoryId: 'all' | string;
  // aspectId?: 'all' | string; // второй уровень KPI (срез по аспекту) — см. закоммент. код в хуке, модели, KPI и dashboard-external-monitoring.tsx
  sentimentKey?: 'all' | 'posNeg' | 'positive' | 'neutral' | 'negative';
};

interface Props {
  filters: ExternalMonitoringFiltersState;
  onFiltersChange: (f: Partial<ExternalMonitoringFiltersState>) => void;
  sourceOptions: SourceOption[];
  categories: ExternalResourceCategory[];
  periodLabel?: string;
}

export const DashboardExternalMonitoringFilters = ({
  filters,
  onFiltersChange,
  sourceOptions,
  categories,
  periodLabel,
}: Props) => {
  const { t } = useLang();

  const sourceSelectOptions = sourceOptions.map((o) => ({
    id: o.id,
    value: o.id === 'all' ? t(o.value) : o.value,
  }));
  const selectedSource =
    sourceSelectOptions.find((o) => o.id === filters.sourceId) ?? sourceSelectOptions[0];

  const rangeDays = externalMonitoringRangeDayCountInclusive(
    filters.periodBeginSec,
    filters.periodEndSec,
  );
  const weekDisabled = rangeDays < EXTERNAL_MONITORING_MIN_DAYS_FOR_WEEK;
  const monthDisabled = rangeDays < EXTERNAL_MONITORING_MIN_DAYS_FOR_MONTH;

  const intervalOptions = [
    { id: 'day', value: t('День') },
    { id: 'week', value: t('Неделя'), disabled: weekDisabled },
    { id: 'month', value: t('Месяц'), disabled: monthDisabled },
  ];
  const selectedInterval =
    intervalOptions.find((o) => o.id === filters.groupingInterval) ?? intervalOptions[0];

  const categorySelectOptions = [
    { id: 'all', value: t('Все категории') },
    ...categories.map((c) => ({ id: String(c.id), value: c.name })),
  ];
  const categoryId = filters.categoryId ?? 'all';
  const selectedCategory =
    categorySelectOptions.find((o) => o.id === categoryId) ?? categorySelectOptions[0];

  return (
    <Card className={s.filtersCard}>
      <div className={s.filtersRow}>
        <Container label={t('Источник')}>
          <Select
            options={sourceSelectOptions}
            value={selectedSource}
            onSelect={(opt) => onFiltersChange({ sourceId: opt.id })}
          />
        </Container>
        <Container label={t('Период')}>
          <ExternalMonitoringPeriodDropdown
            periodBeginSec={filters.periodBeginSec}
            periodEndSec={filters.periodEndSec}
            onRangeChange={(begin, end) =>
              onFiltersChange({ periodBeginSec: begin, periodEndSec: end })
            }
          />
        </Container>
        <Container label={t('Интервал')}>
          <Select
            options={intervalOptions}
            value={selectedInterval}
            onSelect={(opt) =>
              onFiltersChange({
                groupingInterval: opt.id as ExternalMonitoringFiltersState['groupingInterval'],
              })
            }
          />
        </Container>
        <Container label={t('Категория')}>
          <Select
            options={categorySelectOptions}
            value={selectedCategory}
            // При включённом aspectId: onFiltersChange({ categoryId: opt.id, aspectId: 'all' })
            onSelect={(opt) => onFiltersChange({ categoryId: opt.id })}
          />
        </Container>
      </div>
      {periodLabel ? <div className={s.periodHint}>{periodLabel}</div> : null}
    </Card>
  );
};
